import React, { useState } from 'react';
import { Popover, message } from 'antd';
import { Provider } from 'types/provider';
import Logo from 'assets/common/klarity_logo.svg';
import kiwiLogo from 'assets/common/kiwi_logo.svg';
import s from './index.module.less';
import { handleSwitchToAccount as switchAccount } from 'utils/switchAccount';
import { trackHeapEvent } from 'utils/analytics';
import useCurrentUser from 'hooks/useCurrentUser';

export type UserType = 'klarity' | 'kiwi';

interface ProfilePopoverProps {
    provider?: Provider;
    userType?: UserType;
    photoClassName?: string;
    isShowSwitchAccount?: boolean;
    onSwitchToAccount?: () => void;
}

/**
 * 用户头像弹窗组件
 * 显示用户头像，支持hover和click两种触发方式
 * @param provider - 用户信息
 * @param userType - 用户类型，klarity 或 kiwi
 * @param photoClassName - 头像样式类名
 */
const ProfilePopover: React.FC<ProfilePopoverProps> = ({
    provider,
    userType = 'klarity',
    photoClassName,
    isShowSwitchAccount = false,
    onSwitchToAccount,
}) => {
    const [loading, setLoading] = useState(false);
    const isKiwi = userType === 'kiwi';
    const logo = isKiwi ? kiwiLogo : Logo;
    const accountTitle = isKiwi ? 'Kiwi Health account' : 'Klarity Health';
    const userName = provider?.firstName ? `Hi, ${provider.firstName}!` : 'Hi, User!';
    const switchAccountTitle = isKiwi ? 'Klarity Health' : 'Kiwi Health account';
    const [open, setOpen] = useState(false);
    const handleClose = () => {
        trackHeapEvent('WebApp - Profile Popover - Click - Close', {
            userType,
            isKiwi,
        });
        setOpen(false);
    };
    const handleVisibleChange = (newOpen: boolean) => {
        if (newOpen) {
            trackHeapEvent('WebApp - Profile Popover - View - Open', {
                userType,
                isKiwi,
                isShowSwitchAccount,
            });
        } else {
            trackHeapEvent('WebApp - Profile Popover - Click - Close', {
                userType,
                isKiwi,
                isShowSwitchAccount,
            });
        }
        setOpen(newOpen);
    };

    const handleSwitchToAccount = async () => {
        setLoading(true);
        trackHeapEvent('WebApp - Profile Popover - Click - Switch Account Attempt', {
            userType,
            isKiwi,
            fromAccount: isKiwi ? 'Kiwi Health' : 'Klarity Health',
            toAccount: isKiwi ? 'Klarity Health' : 'Kiwi Health',
        });

        try {
            const success = await switchAccount(
                !isKiwi, // isKlarityAccount: 如果当前是kiwi，则切换到klarity
                onSwitchToAccount, // 切换前的回调
            );

            if (success) {
                trackHeapEvent('WebApp - Profile Popover - View - Switch Account Success', {
                    userType,
                    isKiwi,
                    fromAccount: isKiwi ? 'Kiwi Health' : 'Klarity Health',
                    toAccount: isKiwi ? 'Klarity Health' : 'Kiwi Health',
                });
                message.success('Account switched successfully');
            } else {
                trackHeapEvent('WebApp - Profile Popover - View - Switch Account Failed', {
                    userType,
                    isKiwi,
                    fromAccount: isKiwi ? 'Kiwi Health' : 'Klarity Health',
                    toAccount: isKiwi ? 'Klarity Health' : 'Kiwi Health',
                    reason: 'switch_account_failed',
                });
            }
        } catch (error) {
            trackHeapEvent('WebApp - Profile Popover - View - Switch Account Error', {
                userType,
                isKiwi,
                fromAccount: isKiwi ? 'Kiwi Health' : 'Klarity Health',
                toAccount: isKiwi ? 'Klarity Health' : 'Kiwi Health',
                error: error instanceof Error ? error.message : 'Unknown error',
            });
            // eslint-disable-next-line no-console
            console.error('Switch account error:', error);
            message.error('An error occurred while switching account.');
        } finally {
            setLoading(false);
        }
    };

    const renderHoverContent = () => (
        <div className={s.popContentHover}>
            <div className={s.popIcon}>
                <img src={logo} alt={userType} />
            </div>
            <div className={s.popInfo}>
                <div className={s.popTitleHover}>{accountTitle}</div>
                <div className={s.popDesc}>
                    {provider?.firstName} {provider?.lastName}
                </div>
                <div className={s.popEmailHover}>{provider?.email}</div>
            </div>
        </div>
    );

    const renderFigmaContent = () => (
        <div className={s.popContent}>
            <div className={s.popHeader}>
                <div className={s.popTitle}>{accountTitle}</div>
                <button type="button" className={s.closeButton} onClick={handleClose}>
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                        <path
                            d="M12 4L4 12M4 4L12 12"
                            stroke="#9CA3AF"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        />
                    </svg>
                </button>
            </div>
            <div className={s.popUserInfo}>
                <div className={s.popAvatar}>
                    <img src={provider?.photo || logo} alt="Profile" />
                </div>
                <div className={s.popGreeting}>{userName}</div>
                <div className={s.popEmail}>{provider?.email || 'user@example.com'}</div>
            </div>
            {isShowSwitchAccount && (
                <div
                    className={s.popAccountInfo}
                    onClick={handleSwitchToAccount}
                    style={{ cursor: loading ? 'not-allowed' : 'pointer' }}
                >
                    <div className={s.accountLabel}>{switchAccountTitle}</div>
                    <div className={s.accountDetails}>
                        <div className={s.accountIcon}>
                            <img src={provider?.photo || logo} alt={userType} />
                        </div>
                        <div className={s.accountText}>
                            <div className={s.accountName}>
                                {provider?.firstName} {provider?.lastName}
                            </div>
                            <div className={s.accountEmail}>{provider?.email || 'user@example.com'}</div>
                        </div>
                    </div>
                    {loading && (
                        <div className={s.loadingOverlay}>
                            <div className={s.loadingSpinner} />
                        </div>
                    )}
                </div>
            )}
        </div>
    );

    return (
        <Popover
            content={renderFigmaContent()}
            title={null}
            trigger="click"
            placement="topLeft"
            overlayClassName={s.profilePopover}
            open={open}
            onOpenChange={handleVisibleChange}
        >
            <Popover
                content={renderHoverContent()}
                title={null}
                trigger="hover"
                placement="topLeft"
                overlayClassName={s.profilePopoverHover}
            >
                <img
                    className={`${s.profilePhoto} ${photoClassName || ''}`}
                    src={provider?.photo || logo}
                    alt="Profile"
                />
            </Popover>
        </Popover>
    );
};

export default ProfilePopover;
